import React, { useRef, useState, useEffect } from 'react';
import { Viewer, Entity, PointGraphics, PolygonGraphics, Billboard } from 'resium';
import * as Cesium from 'cesium';
import { Button } from '@/components/ui/button';
import { Globe, Map as MapIcon, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import 'cesium/Build/Cesium/Widgets/widgets.css';

const { 
  Cartesian3, 
  Color, 
  Cartographic,
  Math: CesiumMath,
  Ion,
  Terrain,
  EllipsoidTerrainProvider,
  UrlTemplateImageryProvider,
  createWorldTerrainAsync,
  buildModuleUrl
} = Cesium;

// Configure Cesium base URL
if (typeof window !== 'undefined') {
  window.CESIUM_BASE_URL = '/cesium';
}

// Set Cesium Ion access token for high-quality terrain and imagery
Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI3NGNhNjBkMy1iYTM0LTQ5ZGQtYjFlNC01YzkzNGIxYTAwYWYiLCJpZCI6MzYwNzc3LCJpYXQiOjE3NjM5MDA4NzR9.l3YgaQ4kpKKvxkWuUbAHlYhiICDYbZm0OaP44KYGI1E';

const Cesium3DMap = ({ assets = [], areas = [], selectedLayers = {}, onAssetClick, center }) => {
  const viewerRef = useRef(null);
  const [is3D, setIs3D] = useState(true);
  const [terrainEnabled, setTerrainEnabled] = useState(true);
  const [viewerReady, setViewerReady] = useState(false);

  // Handle center prop - can be array [lat, lng] or object {lat, lon, height}
  // Default center to South Goa - lower altitude to see assets
  const defaultCenter = (() => {
    if (!center) {
      return { lat: 15.2832, lon: 73.9882, height: 15000 };
    }
    
    if (Array.isArray(center)) {
      // Leaflet format: [lat, lng]
      return { lat: center[0], lon: center[1], height: 15000 };
    }
    
    // Object format: {lat, lon, height}
    return { 
      lat: center.lat || 15.2832, 
      lon: center.lon || 73.9882, 
      height: center.height || 15000 
    };
  })();

  // Camera position - closer view for asset visibility
  const cameraPosition = Cartesian3.fromDegrees(
    defaultCenter.lon,
    defaultCenter.lat,
    defaultCenter.height
  );

  // Handler for when viewer is ready
  const handleViewerReady = () => {
    console.log('🚀 Cesium viewer ready handler called');
    if (viewerRef.current && viewerRef.current.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      console.log('🚀 Viewer element found, setting up auto-zoom to South Goa');
      
      // Store viewer reference globally for debugging
      window.cesiumViewer = viewer;
      setViewerReady(true);
      
    } else {
      console.error('❌ Viewer element not found in onReady handler');
    }
  };

  // Use useEffect to handle auto-zoom after viewer is ready
  useEffect(() => {
    if (viewerReady && viewerRef.current && viewerRef.current.cesiumElement) {
      const viewer = viewerRef.current.cesiumElement;
      console.log('🚀 useEffect: Setting up auto-zoom to South Goa');
      
      // Fly to South Goa with appropriate view for assets
      const timer = setTimeout(() => {
        console.log('🚀 Executing flyTo animation to South Goa coordinates');
        viewer.camera.flyTo({
          destination: Cartesian3.fromDegrees(73.9882, 15.2832, 15000),
          orientation: {
            heading: CesiumMath.toRadians(0),
            pitch: CesiumMath.toRadians(-45), // Angled view for better 3D perception
            roll: 0
          },
          duration: 2,
          complete: () => {
            console.log('🚀 FlyTo animation completed - should be at South Goa now');
          }
        });
      }, 500); // Increased delay to ensure viewer is fully initialized

      return () => clearTimeout(timer);
    }
  }, [viewerReady]);

  // Toggle between 2D and 3D view
  const toggle3D = () => {
    if (!viewerRef.current || !viewerRef.current.cesiumElement) return;
    
    const viewer = viewerRef.current.cesiumElement;
    
    if (is3D) {
      // Switch to 2D (top-down view)
      viewer.camera.flyTo({
        destination: Cartesian3.fromDegrees(
          73.9882,
          15.2832,
          25000 // Higher altitude for 2D overview
        ),
        orientation: {
          heading: CesiumMath.toRadians(0),
          pitch: CesiumMath.toRadians(-90), // Look straight down
          roll: 0
        },
        duration: 2
      });
    } else {
      // Switch to 3D (angled view)
      viewer.camera.flyTo({
        destination: Cartesian3.fromDegrees(
          73.9882,
          15.2832,
          15000 // Lower altitude to see assets better
        ),
        orientation: {
          heading: CesiumMath.toRadians(0),
          pitch: CesiumMath.toRadians(-45), // Angled view for 3D perception
          roll: 0
        },
        duration: 2
      });
    }
    
    setIs3D(!is3D);
  };

  // Toggle terrain
  const toggleTerrain = () => {
    if (!viewerRef.current || !viewerRef.current.cesiumElement) return;
    
    const viewer = viewerRef.current.cesiumElement;
    
    if (terrainEnabled) {
      // Disable terrain - use flat ellipsoid
      viewer.terrainProvider = new EllipsoidTerrainProvider();
      setTerrainEnabled(false);
    } else {
      // Enable high-quality world terrain from Cesium Ion
      viewer.terrainProvider = Cesium.createWorldTerrainAsync();
      setTerrainEnabled(true);
    }
  };

  // Zoom controls
  const zoomIn = () => {
    if (!viewerRef.current || !viewerRef.current.cesiumElement) return;
    const viewer = viewerRef.current.cesiumElement;
    viewer.camera.zoomIn(viewer.camera.positionCartographic.height * 0.5);
  };

  const zoomOut = () => {
    if (!viewerRef.current || !viewerRef.current.cesiumElement) return;
    const viewer = viewerRef.current.cesiumElement;
    viewer.camera.zoomOut(viewer.camera.positionCartographic.height * 0.5);
  };

  const resetView = () => {
    if (!viewerRef.current || !viewerRef.current.cesiumElement) return;
    const viewer = viewerRef.current.cesiumElement;
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(73.9882, 15.2832, 15000),
      orientation: {
        heading: CesiumMath.toRadians(0),
        pitch: CesiumMath.toRadians(-45),
        roll: 0
      },
      duration: 2
    });
  };

  // Filter assets based on selected layers
  const shouldShowAsset = (asset) => {
    if (asset.type === 'Reservoir' && !selectedLayers.reservoirs) return false;
    if (asset.type === 'WTP' && !selectedLayers.wtp) return false;
    if (asset.type === 'OHT' && !selectedLayers.oht) return false;
    if (asset.type === 'Pipeline' && !selectedLayers.pipelines) return false;
    if (asset.type === 'DMA' && !selectedLayers.dma) return false;
    if (asset.type === 'PumpHouse' && !selectedLayers.pumphouse) return false;
    return true;
  };

  const filteredAssets = assets.filter(shouldShowAsset);

  // Get color based on asset status
  const getAssetColor = (status) => {
    switch (status) {
      case 'Operational':
        return Color.GREEN;
      case 'Under Maintenance':
        return Color.ORANGE;
      case 'Non-Functional':
        return Color.RED;
      default:
        return Color.GRAY;
    }
  };

  // Get boundary styling
  const getBoundaryColor = (type) => {
    switch (type) {
      case 'District':
        return Color.RED.withAlpha(0.3);
      case 'Taluka':
        return Color.BLUE.withAlpha(0.3);
      case 'Village':
      case 'Panchayat':
        return Color.GREEN.withAlpha(0.2);
      default:
        return Color.GRAY.withAlpha(0.2);
    }
  };

  const getBoundaryOutlineColor = (type) => {
    switch (type) {
      case 'District':
        return Color.RED;
      case 'Taluka':
        return Color.BLUE;
      case 'Village':
      case 'Panchayat':
        return Color.GREEN;
      default:
        return Color.GRAY;
    }
  };

  const getBoundaryOutlineWidth = (type) => {
    switch (type) {
      case 'District':
        return 4;
      case 'Taluka':
        return 2;
      case 'Village':
      case 'Panchayat':
        return 1.5;
      default:
        return 1;
    }
  };

  // Separate areas by type
  const districts = areas.filter(a => a.type === 'District' && a.boundary);
  const talukas = areas.filter(a => a.type === 'Taluka' && a.boundary);
  const villagesWithBoundary = areas.filter(a => (a.type === 'Village' || a.type === 'Panchayat') && a.boundary);
  const villagePoints = areas.filter(a => (a.type === 'Village' || a.type === 'Panchayat') && a.latitude && a.longitude && !a.boundary);

  return (
    <div style={{ position: 'relative', width: '100%', height: '600px' }}>
      {/* Control Panel */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        <Button
          onClick={toggle3D}
          className="bg-blue-600 hover:bg-blue-700"
          title={is3D ? 'Switch to 2D View' : 'Switch to 3D View'}
        >
          {is3D ? <MapIcon size={16} /> : <Globe size={16} />}
          <span className="ml-2">{is3D ? '2D' : '3D'}</span>
        </Button>
        
        <Button
          onClick={zoomIn}
          className="bg-slate-700 hover:bg-slate-600"
          title="Zoom In"
        >
          <ZoomIn size={16} />
        </Button>
        
        <Button
          onClick={zoomOut}
          className="bg-slate-700 hover:bg-slate-600"
          title="Zoom Out"
        >
          <ZoomOut size={16} />
        </Button>
        
        <Button
          onClick={resetView}
          className="bg-slate-700 hover:bg-slate-600"
          title="Reset View"
        >
          <Maximize2 size={16} />
        </Button>
      </div>

      {/* Cesium Viewer */}
      <Viewer
        ref={(viewer) => {
          viewerRef.current = viewer;
          if (viewer && viewer.cesiumElement) {
            console.log('🚀 Viewer ref callback called');
            const cesiumViewer = viewer.cesiumElement;
            window.cesiumViewer = cesiumViewer;
            
            // Set up auto-zoom immediately
            setTimeout(() => {
              console.log('🚀 Executing flyTo from ref callback');
              cesiumViewer.camera.flyTo({
                destination: Cartesian3.fromDegrees(73.9882, 15.2832, 15000),
                orientation: {
                  heading: CesiumMath.toRadians(0),
                  pitch: CesiumMath.toRadians(-45),
                  roll: 0
                },
                duration: 2,
                complete: () => {
                  console.log('🚀 FlyTo completed from ref callback');
                }
              });
            }, 1000);
          }
        }}
        full={false}
        style={{ width: '100%', height: '100%' }}
        animation={false}
        baseLayerPicker={false}
        fullscreenButton={false}
        geocoder={false}
        homeButton={false}
        infoBox={true}
        sceneModePicker={false}
        selectionIndicator={true}
        timeline={false}
        navigationHelpButton={false}
        terrainProvider={Cesium.createWorldTerrainAsync()}
      >
        {/* Boundary lines removed as per user request */}

        {/* Village Points */}
        {villagePoints.map((village) => {
          // Ensure coordinates are valid numbers
          if (typeof village.latitude !== 'number' || typeof village.longitude !== 'number') {
            return null;
          }
          
          try {
            return (
              <Entity
                key={`village-point-${village.id}`}
                name={village.name}
                description={`<h3>${village.name}</h3><p>Type: Village</p>${village.population ? `<p>Population: ${village.population.toLocaleString()}</p>` : ''}`}
                position={Cartesian3.fromDegrees(village.longitude, village.latitude, 0)}
              >
                <PointGraphics
                  pixelSize={12}
                  color={Color.BLUE}
                  outlineColor={Color.DARKBLUE}
                  outlineWidth={3}
                />
              </Entity>
            );
          } catch (error) {
            console.warn(`Error rendering village point for ${village.name}:`, error);
            return null;
          }
        }).filter(Boolean)}

        {/* Water Infrastructure Assets */}
        {filteredAssets.map((asset) => {
          // Ensure coordinates are valid numbers
          if (typeof asset.latitude !== 'number' || typeof asset.longitude !== 'number') {
            return null;
          }
          
          try {
            return (
              <Entity
                key={`asset-${asset.id}`}
                name={asset.name}
                description={`
                  <h3>${asset.name}</h3>
                  <p><strong>Type:</strong> ${asset.type}</p>
                  <p><strong>Status:</strong> ${asset.status}</p>
                  ${asset.capacity ? `<p><strong>Capacity:</strong> ${asset.capacity} L</p>` : ''}
                  <p><strong>Location:</strong> ${asset.latitude.toFixed(4)}, ${asset.longitude.toFixed(4)}</p>
                `}
                position={Cartesian3.fromDegrees(asset.longitude, asset.latitude, 100)}
                onClick={() => onAssetClick && onAssetClick(asset)}
              >
                <PointGraphics
                  pixelSize={10}
                  color={getAssetColor(asset.status)}
                  outlineColor={Color.WHITE}
                  outlineWidth={2}
                />
              </Entity>
            );
          } catch (error) {
            console.warn(`Error rendering asset ${asset.name}:`, error);
            return null;
          }
        }).filter(Boolean)}
      </Viewer>
    </div>
  );
};

export default Cesium3DMap;
